/**
 * Human labels for Global_Locations (Feature: soroush-platform, Req 8.2).
 *
 * A Check's `locations` are the AWS regions it runs **from** — the vantage point of the probe, not
 * anything about the target. `eu-central-1` says nothing to most readers; "Frankfurt" does.
 *
 * This map is **presentation**, which is why it lives in the SPA rather than in `@soroush/core` or the
 * runtime config: which regions a deployment provisions is a deployment fact (it arrives as
 * `runnerRegions` in `config.json`), but what to *call* them is not. A region absent from this map
 * renders as its own identifier, so an unlabelled region is merely unadorned rather than invisible —
 * the picker must never silently omit a location the platform will actually run from.
 */

/** City labels for the regions the platform is likely to run from. Extend as regions are added. */
const REGION_CITY: Readonly<Record<string, string>> = {
  'eu-central-1': 'Frankfurt',
  'eu-west-1': 'Ireland',
  'eu-west-2': 'London',
  'eu-west-3': 'Paris',
  'eu-north-1': 'Stockholm',
  'eu-south-1': 'Milan',
  'eu-south-2': 'Zaragoza',
  'us-east-1': 'N. Virginia',
  'us-east-2': 'Ohio',
  'us-west-1': 'N. California',
  'us-west-2': 'Oregon',
  'ap-southeast-1': 'Singapore',
  'ap-southeast-2': 'Sydney',
  'ap-northeast-1': 'Tokyo',
  'ap-south-1': 'Mumbai',
  'sa-east-1': 'São Paulo',
};

/**
 * How a location is offered in a picker: the city where known, with the region id kept alongside.
 *
 * Both halves deliberately. The city is what a reader understands; the region id is what the API
 * stores, what a manifest names, and what any support conversation will refer to — dropping it would
 * make the UI and the data speak different languages.
 */
export function locationLabel(location: string): string {
  const city = REGION_CITY[location];
  return city === undefined ? location : `${city} (${location})`;
}

/** The city alone, or the region id when unlabelled. For dense contexts like a results table. */
export function locationShortLabel(location: string): string {
  return REGION_CITY[location] ?? location;
}
